
import React, { createContext, useContext, useState } from 'react';
import { useTestSecurity } from '@/hooks/useTestSecurity';
import { toast } from 'sonner';

interface TestSecurityContextType {
  violations: {
    total: number;
    [key: string]: number;
  };
  isLocked: boolean;
  isFullscreen: boolean;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
  resetViolations: () => void;
  unlockTest: () => void;
}

const TestSecurityContext = createContext<TestSecurityContextType>({
  violations: { total: 0 },
  isLocked: false,
  isFullscreen: false,
  enterFullscreen: () => {},
  exitFullscreen: () => {},
  resetViolations: () => {},
  unlockTest: () => {},
});

export const useTestSecurityContext = () => useContext(TestSecurityContext);

interface TestSecurityProviderProps {
  children: React.ReactNode;
}

export const TestSecurityProvider: React.FC<TestSecurityProviderProps> = ({ children }) => {
  const [showSecurityWarning, setShowSecurityWarning] = useState(false);
  
  // Apply full test security measures
  const { 
    violations: rawViolations, 
    isLocked, 
    isFullscreen,
    enterFullscreen,
    exitFullscreen,
    resetViolations,
    unlockTest 
  } = useTestSecurity({
    onViolation: (type, count) => {
      if (count === 1) {
        toast.warning(
          "Security alert: Test integrity violation detected.",
          { 
            description: "Further violations will lock your test.",
            duration: 4000 
          }
        );
      }
    },
    maxViolations: 3,
    enableFullscreen: true,
    blockKeyboardShortcuts: true,
    disableF12Key: true,
    preventTabSwitching: true,
    disableRightClick: true,
    preventResize: true
  });

  // Ensure violations always has a total property
  const violations = {
    ...rawViolations,
    total: typeof rawViolations.total === 'number' ? rawViolations.total : 0
  };

  // Show security warning when first rendering a test
  React.useEffect(() => {
    if (!showSecurityWarning) {
      setShowSecurityWarning(true);
      toast.info(
        "Test security activated",
        {
          description: "This test has enhanced security to maintain integrity. Tab switching, window minimizing, and keyboard shortcuts are disabled.",
          duration: 6000,
          action: {
            label: "Start Test",
            onClick: () => enterFullscreen()
          }
        }
      );
    }
  }, [enterFullscreen, showSecurityWarning]);
  
  return (
    <TestSecurityContext.Provider 
      value={{ 
        violations, 
        isLocked,
        isFullscreen,
        enterFullscreen,
        exitFullscreen,
        resetViolations,
        unlockTest
      }}
    >
      {children}
    </TestSecurityContext.Provider>
  );
};
